import { useState } from 'react';
import { motion } from 'motion/react';
import { ArrowLeft, Check, Search } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { FlagImage } from './utils/countryFlags';
import { toast } from 'sonner';
import { ParallaxBackground } from './utils/parallax';

interface Country {
  name: string;
  code: string;
  region: string;
}

interface CountrySelectionScreenProps {
  currentCountry: string;
  onBack: () => void;
  onSave: (country: { name: string; code: string; flag: string }) => void;
}

// All countries with regions
const allCountries: Country[] = [
  // African Countries (North Africa)
  { name: 'Algeria', code: 'dz', region: 'North Africa' },
  { name: 'Egypt', code: 'eg', region: 'North Africa' },
  { name: 'Libya', code: 'ly', region: 'North Africa' },
  { name: 'Morocco', code: 'ma', region: 'North Africa' },
  { name: 'Sudan', code: 'sd', region: 'North Africa' },
  { name: 'Tunisia', code: 'tn', region: 'North Africa' },

  // West Africa
  { name: 'Benin', code: 'bj', region: 'West Africa' },
  { name: 'Burkina Faso', code: 'bf', region: 'West Africa' },
  { name: 'Cape Verde', code: 'cv', region: 'West Africa' },
  { name: 'Ivory Coast', code: 'ci', region: 'West Africa' },
  { name: 'Gambia', code: 'gm', region: 'West Africa' },
  { name: 'Ghana', code: 'gh', region: 'West Africa' },
  { name: 'Guinea', code: 'gn', region: 'West Africa' },
  { name: 'Guinea-Bissau', code: 'gw', region: 'West Africa' },
  { name: 'Liberia', code: 'lr', region: 'West Africa' },
  { name: 'Mali', code: 'ml', region: 'West Africa' },
  { name: 'Mauritania', code: 'mr', region: 'West Africa' },
  { name: 'Niger', code: 'ne', region: 'West Africa' },
  { name: 'Nigeria', code: 'ng', region: 'West Africa' },
  { name: 'Senegal', code: 'sn', region: 'West Africa' },
  { name: 'Sierra Leone', code: 'sl', region: 'West Africa' },
  { name: 'Togo', code: 'tg', region: 'West Africa' },

  // East Africa
  { name: 'Burundi', code: 'bi', region: 'East Africa' },
  { name: 'Comoros', code: 'km', region: 'East Africa' },
  { name: 'Djibouti', code: 'dj', region: 'East Africa' },
  { name: 'Eritrea', code: 'er', region: 'East Africa' },
  { name: 'Ethiopia', code: 'et', region: 'East Africa' },
  { name: 'Kenya', code: 'ke', region: 'East Africa' },
  { name: 'Madagascar', code: 'mg', region: 'East Africa' },
  { name: 'Malawi', code: 'mw', region: 'East Africa' },
  { name: 'Mauritius', code: 'mu', region: 'East Africa' },
  { name: 'Mozambique', code: 'mz', region: 'East Africa' },
  { name: 'Rwanda', code: 'rw', region: 'East Africa' },
  { name: 'Seychelles', code: 'sc', region: 'East Africa' },
  { name: 'Somalia', code: 'so', region: 'East Africa' },
  { name: 'South Sudan', code: 'ss', region: 'East Africa' },
  { name: 'Tanzania', code: 'tz', region: 'East Africa' },
  { name: 'Uganda', code: 'ug', region: 'East Africa' },
  { name: 'Zambia', code: 'zm', region: 'East Africa' },
  { name: 'Zimbabwe', code: 'zw', region: 'East Africa' },

  // Central Africa
  { name: 'Angola', code: 'ao', region: 'Central Africa' },
  { name: 'Cameroon', code: 'cm', region: 'Central Africa' },
  { name: 'CAR', code: 'cf', region: 'Central Africa' },
  { name: 'Chad', code: 'td', region: 'Central Africa' },
  { name: 'Congo', code: 'cg', region: 'Central Africa' },
  { name: 'DR Congo', code: 'cd', region: 'Central Africa' },
  { name: 'Equatorial Guinea', code: 'gq', region: 'Central Africa' },
  { name: 'Gabon', code: 'ga', region: 'Central Africa' },
  { name: 'São Tomé', code: 'st', region: 'Central Africa' },

  // Southern Africa
  { name: 'Botswana', code: 'bw', region: 'Southern Africa' },
  { name: 'eSwatini', code: 'sz', region: 'Southern Africa' },
  { name: 'Lesotho', code: 'ls', region: 'Southern Africa' },
  { name: 'Namibia', code: 'na', region: 'Southern Africa' },
  { name: 'South Africa', code: 'za', region: 'Southern Africa' },

  // Caribbean Nations
  { name: 'Antigua and Barbuda', code: 'ag', region: 'Caribbean' },
  { name: 'Bahamas', code: 'bs', region: 'Caribbean' },
  { name: 'Barbados', code: 'bb', region: 'Caribbean' },
  { name: 'Dominica', code: 'dm', region: 'Caribbean' },
  { name: 'Grenada', code: 'gd', region: 'Caribbean' },
  { name: 'Haiti', code: 'ht', region: 'Caribbean' },
  { name: 'Jamaica', code: 'jm', region: 'Caribbean' },
  { name: 'Saint Lucia', code: 'lc', region: 'Caribbean' },
  { name: 'Saint Vincent and the Grenadines', code: 'vc', region: 'Caribbean' },
  { name: 'Trinidad and Tobago', code: 'tt', region: 'Caribbean' },
  { name: 'Belize', code: 'bz', region: 'Caribbean' },
  { name: 'Cuba', code: 'cu', region: 'Caribbean' },
  { name: 'Dominican Republic', code: 'do', region: 'Caribbean' },
  { name: 'Guyana', code: 'gy', region: 'Caribbean' },
  { name: 'Saint Kitts and Nevis', code: 'kn', region: 'Caribbean' },
  { name: 'Guadeloupe', code: 'gp', region: 'Caribbean' },
  { name: 'Martinique', code: 'mq', region: 'Caribbean' },
  { name: 'French Guiana', code: 'gf', region: 'Caribbean' },
  { name: 'Puerto Rico', code: 'pr', region: 'Caribbean' },
  { name: 'Curacao', code: 'cw', region: 'Caribbean' },
  { name: 'Aruba', code: 'aw', region: 'Caribbean' },
  { name: 'Bonaire', code: 'bq', region: 'Caribbean' },
  { name: 'Bermuda', code: 'bm', region: 'Caribbean' },
  { name: 'Cayman Islands', code: 'ky', region: 'Caribbean' },
  { name: 'Turks and Caicos Islands', code: 'tc', region: 'Caribbean' },
  { name: 'US Virgin Islands', code: 'vi', region: 'Caribbean' },
  { name: 'Anguilla', code: 'ai', region: 'Caribbean' },
  { name: 'Montserrat', code: 'ms', region: 'Caribbean' },
  { name: 'Mayotte', code: 'yt', region: 'Africa' },
  { name: 'Saint Helena', code: 'sh', region: 'Africa' },
  { name: 'Suriname', code: 'sr', region: 'Caribbean' },

  // Diaspora Countries / Regional
  { name: 'Black American/Soulaan', code: 'soulaan', region: 'North American' },
  { name: 'United States', code: 'us', region: 'North American' },
  { name: 'Canada', code: 'ca', region: 'North American' },
  { name: 'Republic of New Afrika', code: 'rna', region: 'North American' },
  { name: 'Gullah Geechie', code: 'gg', region: 'North American' },
  { name: 'United Kingdom', code: 'gb', region: 'European' },
  { name: 'France', code: 'fr', region: 'European' },
  { name: 'Germany', code: 'de', region: 'European' },
  { name: 'Netherlands', code: 'nl', region: 'European' },
  { name: 'Belgium', code: 'be', region: 'European' },
  { name: 'Portugal', code: 'pt', region: 'European' },
  { name: 'Spain', code: 'es', region: 'European' },
  { name: 'Italy', code: 'it', region: 'European' },
  { name: 'Sweden', code: 'se', region: 'European' },
  { name: 'Brazil', code: 'br', region: 'South American' },
  { name: 'Australia', code: 'au', region: 'Oceania' },
  { name: 'Palestine', code: 'ps', region: 'Asian' },
  { name: 'Israel', code: 'il', region: 'Asian' },
];

export function CountrySelectionScreen({ currentCountry, onBack, onSave }: CountrySelectionScreenProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);

  // Filter countries based on search
  const filteredCountries = allCountries.filter(country =>
    country.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    country.region.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Group countries by region
  const groupedCountries = filteredCountries.reduce((acc, country) => {
    if (!acc[country.region]) {
      acc[country.region] = [];
    }
    acc[country.region].push(country);
    return acc;
  }, {} as Record<string, Country[]>);

  const handleSelectCountry = (country: Country) => {
    setSelectedCountry(country);
  };

  const handleSave = () => {
    if (!selectedCountry) {
      toast.error('Please select a country or region');
      return;
    }

    onSave({
      name: selectedCountry.name,
      code: selectedCountry.code,
      flag: selectedCountry.code
    });
  };

  const FlagDisplay = ({ country }: { country: Country }) => (
    <FlagImage countryName={country.name} size="w80" className="w-12 h-8 rounded object-contain" />
  );

  return (
    <div className="w-full h-screen bg-light-pattern dark:bg-[#0D0D0D] relative">
      <div className="absolute inset-0 pointer-events-none">
        <ParallaxBackground speed={0.05} />
      </div>
      <div className="w-full max-w-2xl mx-auto h-full flex flex-col relative z-10">
        {/* Header */}
        <div className="p-4 border-b border-black/10 dark:border-white/10 bg-white/80 dark:bg-black/30 backdrop-blur-lg"
          style={{ paddingTop: 'max(60px, calc(env(safe-area-inset-top) + 24px))' }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={onBack}
                className="text-[#2C3E50] dark:text-[#E5E5E5] hover:text-black dark:hover:text-white"
              >
                <ArrowLeft className="w-6 h-6" />
              </motion.button>
              <h1 className="text-2xl text-black dark:text-white">
                Select Country or Region
              </h1>
            </div>
            <Button
              onClick={handleSave}
              disabled={!selectedCountry}
              className="bg-linear-to-r from-[#F39C12] to-[#E74C3C] dark:from-[#4478ea] dark:to-[#2d1ef1] hover:opacity-90 text-white disabled:opacity-50"
            >
              <Check className="w-4 h-4 mr-2" />
              Save
            </Button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="p-4 bg-white/50 dark:bg-black/20">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#666666] dark:text-[#a0a0a0]" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search countries or regions..."
              className="pl-10 bg-white dark:bg-[#1a1a1a] border-black/10 dark:border-white/20 text-black dark:text-white"
            />
          </div>
        </div>

        {/* Countries List */}
        <ScrollArea className="flex-1 bg-white/30 dark:bg-black/10">
          <div className="p-4 space-y-6">
            {Object.entries(groupedCountries).map(([region, countries]) => (
              <div key={region}>
                <h2 className="text-lg text-black dark:text-white mb-3 px-2">{region}</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {countries.map((country) => (
                    <motion.button
                      key={country.code}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleSelectCountry(country)}
                      className={`p-4 rounded-xl border-2 transition-all ${selectedCountry?.code === country.code
                        ? 'border-[#F39C12] bg-[#F39C12]/10 dark:border-[#4478ea] dark:bg-[#4478ea]/20'
                        : 'border-black/10 dark:border-white/10 bg-white dark:bg-[#1a1a1a] hover:border-[#F39C12]/50 dark:hover:border-[#4478ea]/50'
                        }`}
                    >
                      <div className="flex items-center gap-3">
                        <FlagDisplay country={country} />
                        <div className="flex-1 text-left">
                          <p className="text-black dark:text-white">{country.name}</p>
                          <p className="text-sm text-[#666666] dark:text-[#a0a0a0]">{country.region}</p>
                        </div>
                        {selectedCountry?.code === country.code && (
                          <Check className="w-5 h-5 text-[#F39C12] dark:text-[#4478ea]" />
                        )}
                      </div>
                    </motion.button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>
      </div>
    </div>
  );
}
